import Label from "./Label";
import {Label as LabelInterface} from "../types/entities";
import React, {MouseEventHandler, useEffect, useRef, useState} from "react";
import {WithTranslation, withTranslation} from "react-i18next";
import {useScene} from "../providers/SceneProvider";
import Api from "../Api";
import Spinner from "./Spinner";
import {Button} from "../styled-components/Button";
import {Fade} from "@mui/material";
import Icon from "./Icon";
import {faChevronDown, faChevronUp, faCircleInfo} from "@fortawesome/free-solid-svg-icons";
import {HtmlTooltipLight} from "./HtmlTooltipLight";
import {i18nStringFromObject} from "../plugins/i18n";
import {isMobile} from "react-device-detect";

interface LabelSkeletonProps extends WithTranslation {
    label: LabelInterface
    alreadyExpanded?: boolean
    isDemo: boolean
}

function LabelSkeleton({label, alreadyExpanded, isDemo, t, tReady, i18n}: LabelSkeletonProps) {
    const scene = useScene();
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const labelRef = useRef(null)
    const selectedLabelRef = useRef(null)
    const [showTooltip, setShowTooltip] = useState(false);

    const searchTerm = (scene.labelFilter || '').toLowerCase().trim();
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const userCollapsedRef = useRef<boolean>(false);
    const autoExpandedTermRef = useRef<string | null>(null);
    const [refreshVersion, setRefreshVersion] = useState(0);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearchTerm(searchTerm), 100);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    // Reset user collapse guard when search term changes
    useEffect(() => {
        userCollapsedRef.current = false;
        autoExpandedTermRef.current = null;
    }, [debouncedSearchTerm]);

    const doesLabelOrDescendantMatch = (lbl: LabelInterface): boolean => {
        if (!debouncedSearchTerm) return true;
        const title = i18nStringFromObject(lbl, 'title').toLowerCase();
        if (title.includes(debouncedSearchTerm)) return true;
        if (lbl.labels && lbl.labels.length > 0) {
            return lbl.labels.some(child => doesLabelOrDescendantMatch(child));
        }
        return false;
    };

    // During search, expand all nodes with children to discover matches
    useEffect(() => {
        if (debouncedSearchTerm && !userCollapsedRef.current && label.labelCount > 0 && !expanded) {
            setExpanded(true);
        }
    }, [debouncedSearchTerm, label.labelCount, expanded]);

    const selectLabel = () => {
        if(label.labelCount === 0 || expanded) {
            let newLabelArr = Object.assign([], scene.selectedLabel ?? []);

            if (newLabelArr.find(l => l.id === label.id)) {
                // remove clicked label if label exists in array
                newLabelArr = newLabelArr.filter(l => l.id !== label.id);
            } else {
                if (newLabelArr.length < scene.batch.maxLabels || scene.batch.maxLabels === 0) {
                    // add clicked label if label does not exist in array
                    newLabelArr.push(label)
                }
            }
            scene.setSelectedLabel(newLabelArr);
        }
        setExpanded(true)
    }

    const containsObject = (): boolean => {
        return scene.selectedLabel && !!scene.selectedLabel.find(l => l.id === label.id);
    }

    useEffect(() => {
        if (expanded && label.labelCount > 0) {
            const fetchChildren = () => Api.get(`${isDemo ? '/demo' : ''}/labels/${label.id}/labels`,
                ({data}) => {
                    label.labels = data.items;
                    setLoading(false);

                    // During search, load second level unfiltered, then client-filter to exact path
                    if (debouncedSearchTerm && Array.isArray(label.labels) && label.labels.length > 0) {
                        const childrenToLoad = label.labels.filter((child) => child.labelCount > 0 && (!child.labels || child.labels.length !== child.labelCount));
                        if (childrenToLoad.length > 0) {
                            let pending = childrenToLoad.length;
                            setLoading(true);
                            childrenToLoad.forEach((child) => {
                                const url = `${isDemo ? '/demo' : ''}/labels/${child.id}/labels`;
                                Api.get(url, ({data: childData}) => {
                                    child.labels = childData.items;
                                    setRefreshVersion(v => v + 1);
                                    pending -= 1;
                                    if (pending === 0) setLoading(false);
                                });
                            });
                        }
                    }

                    // Scroll into view when relevant
                    if (debouncedSearchTerm && !userCollapsedRef.current) {
                        const selfMatches = i18nStringFromObject(label, 'title').toLowerCase().includes(debouncedSearchTerm);
                        const hasMatchingChild = label.labels.some(childLabel => doesLabelOrDescendantMatch(childLabel));
                        if (selfMatches || hasMatchingChild) {
                            selectedLabelRef.current?.scrollIntoView({behavior: "smooth", block: "center"});
                        }
                    } else {
                        selectedLabelRef.current?.scrollIntoView({behavior: "smooth", block: "center"});
                    }
                });

            if (label.labels == null || label.labelCount !== label.labels.length) {
                setLoading(true);
                fetchChildren();
            }
        }
    }, [expanded, debouncedSearchTerm, refreshVersion])

    const toggleTooltip = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        setShowTooltip(!showTooltip)
    }

    const toggleExpanded = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        const next = !expanded;
        // If user collapses during search, keep it collapsed and suppress auto-expand
        if (!next) {
            userCollapsedRef.current = true;
        } else {
            // User explicitly expanded; allow auto-expansion again for current term
            userCollapsedRef.current = false;
        }
        setExpanded(next);
    }

    return (
        <div ref={labelRef}>
            <ul className='space-y-1 h-full w-full list'>
                <Label ref={selectedLabelRef} onClick={() => selectLabel()}
                       selected={containsObject()}>
                    <div>{ i18nStringFromObject(label, 'title') }</div>
                    <div  className='flex space-x-2.5 items-center'>
                        <HtmlTooltipLight  open={showTooltip}
                                           onOpen={() => setShowTooltip(true)}
                                           onClose={() => setShowTooltip(false)}
                                           TransitionComponent={Fade}
                                           enterDelay={0}
                                           enterTouchDelay={0}
                                           title={ i18nStringFromObject(label, 'description') }
                                           tooltipClasses={'bg-red-500'}
                                           leaveTouchDelay={1500}
                                           className={`!-mt-[4vw] mini-mobile:!-mt-[2.5vw] tablet:!-mt-0`}
                                           arrow>
                            <Button className='cursor-default'><Icon icon={faCircleInfo}/></Button>
                        </HtmlTooltipLight>
                        <div className='flex items-center w-4 h-4' onClick={toggleExpanded}>
                            {label.labelCount > 0 && (
                                <Icon icon={expanded ? faChevronUp : faChevronDown} />
                            )}
                        </div>
                    </div>
                </Label>
                {loading && <div className='flex justify-center'>
                    <Spinner/>
                </div>}

                <li className={expanded ? 'pl-5 space-y-1 labelItem' : 'pl-5 space-y-1 hidden labelItem'}>
                    {label.labels?.filter((child: LabelInterface) => {
                        return debouncedSearchTerm ? doesLabelOrDescendantMatch(child) : true;
                    }).map((childLabel: LabelInterface, key) => {
                        return <LabelSkeleton isDemo={isDemo} alreadyExpanded={alreadyExpanded} key={key} label={childLabel} t={t}
                                              tReady={tReady} i18n={i18n}/>
                    })}
                </li>
            </ul>
        </div>
    )
}

export default withTranslation()(LabelSkeleton)